import type {
  IGhCardActionEvent,
  IGhCardActionResult,
  IGhPayload,
} from '../gpthelp-host-contract';
import type { ICardFormat } from '../cards/card-text';
import { payloadKind } from '../cards/proposal-cards';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpToolGate,
  IGpthelpToolScope,
} from '../tools/tool-types';
import { readAssistantError } from './assistant-errors';
import { refusalCard } from './assistant-refusals';

/** What the card actions need from the admin. */
export interface IAssistantCardActionHost {
  deps: ICartadigitalAdminHostToolDeps;
  /** `navigator.onLine`. */
  isOnline(): boolean;
  /** Reloads the admin (a newer version is out). */
  reload(): void;
  /** Opens a link outside the admin (the in-app browser in the APK). */
  openExternal(url: string): Promise<void>;
}

/** One card action with the store, branch and format it runs in. */
export interface IActionRun {
  event: IGhCardActionEvent;
  payload: IGhPayload;
  host: IAssistantCardActionHost;
  scope: IGpthelpToolScope;
  gate: IGpthelpToolGate;
  fmt: ICardFormat;
}

/** Characters of a notice under a card (host contract §3.4). */
export const NOTICE_MAX = 160;

/** A non-empty string of a card payload; null for anything else. */
export function payloadString(
  value: IGhPayload[string] | undefined
): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** The non-empty string `key` of the payload; a card without it is malformed. */
export function requirePayloadString(run: IActionRun, key: string): string {
  const value = payloadString(run.payload[key]);
  if (!value)
    throw new Error(`The ${run.event.actionId} action has no ${key}.`);
  return value;
}

/**
 * The card that replaces the tapped one after an Action API refusal the
 * member should read; null for the others. The tapped card's payload tells
 * an inventory change (a proposal's card, plan P5-3).
 */
export function refusalResult(
  error: unknown,
  run: IActionRun
): IGhCardActionResult | null {
  const card = refusalCard(
    readAssistantError(error),
    run.event.cardId,
    run.gate,
    run.fmt,
    payloadKind(run.payload)
  );
  return card ? { card } : null;
}
